'use client';

import { useEffect, useState } from 'react';
import { getPaydirtProductStats, type PaydirtProductStats } from './api';

export interface PaydirtProductStatsPanelProps {
  vendorUserId: string;
  productId: string;
}

function formatUsd(n: number) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}

export function PaydirtProductStatsPanel({ vendorUserId, productId }: PaydirtProductStatsPanelProps) {
  const [stats, setStats] = useState<PaydirtProductStats | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getPaydirtProductStats(vendorUserId, productId)
      .then(setStats)
      .catch((err) => {
        setStats(null);
        setError(err instanceof Error ? err.message : 'Could not load haul stats');
      });
  }, [vendorUserId, productId]);

  if (error) return null;
  if (!stats || stats.haulCount === 0) {
    return (
      <p className="text-muted vendor-paydirt-stats">
        No linked haul reports yet for this bag listing.
      </p>
    );
  }

  return (
    <dl className="vendor-paydirt-stats">
      <div>
        <dt>Community hauls</dt>
        <dd>{stats.haulCount}</dd>
      </div>
      <div>
        <dt>Avg metals / bag</dt>
        <dd>
          {stats.avgMetalsTroyOzPerPurchase.toFixed(4)} troy oz
          {stats.avgMetalsValueUsdPerPurchase > 0 && (
            <span className="text-muted"> ({formatUsd(stats.avgMetalsValueUsdPerPurchase)} est.)</span>
          )}
        </dd>
      </div>
      <div>
        <dt>Avg precious gems / bag</dt>
        <dd>{stats.avgPreciousGemCaratsPerPurchase.toFixed(2)} ct</dd>
      </div>
      <div>
        <dt>Avg semi-precious gems / bag</dt>
        <dd>
          {stats.avgSemiPreciousGramsPerPurchase > 0
            ? `${stats.avgSemiPreciousGramsPerPurchase.toFixed(2)} g`
            : '—'}
        </dd>
      </div>
    </dl>
  );
}
